import { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';
import LanguageSwitcher from '../../components/LanguageSwitcher';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { ScrollArea } from '../../components/ui/scroll-area';
import { Badge } from '../../components/ui/badge';
import {
  Send, Paperclip, ArrowLeft, Loader2, MessageSquare,
  FileText, X, Download, Search, LogOut, Volume2, VolumeX,
  Bell, BellOff,
} from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '../../lib/utils';
import { playMessageDing, unlockAudio, isSoundEnabled, setSoundEnabled } from '../../lib/notifySound';
import { notifyIncomingMessage } from '../../lib/tabAlert';
import { ensurePushSubscription, isPushSupported } from '../../lib/pushClient';
import { setAppBadge, clearAppBadge } from '../../lib/appBadge';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const LabChat = () => {
  const { t, i18n } = useTranslation();
  const { channelId } = useParams();
  const navigate = useNavigate();
  const { user, logout, heartbeat } = useAuth();
  const [channels, setChannels] = useState([]);
  const [messages, setMessages] = useState([]);
  const [clientName, setClientName] = useState('');
  const [clientInfo, setClientInfo] = useState({ first_name: '', last_name: '', structure: '', city: '' });
  const [clientOnline, setClientOnline] = useState(false);
  const [newMessage, setNewMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState(null);
  const [searching, setSearching] = useState(false);
  const [typingUsers, setTypingUsers] = useState([]);
  const [soundOn, setSoundOn] = useState(isSoundEnabled());
  const [pushEnabled, setPushEnabled] = useState(
    typeof Notification !== 'undefined' && Notification.permission === 'granted'
  );
  const messagesEndRef = useRef(null);
  const pollRef = useRef(null);
  const heartbeatRef = useRef(null);
  const fileInputRef = useRef(null);
  const typingThrottleRef = useRef(0);
  const lastIncomingIdRef = useRef(null);
  const initialLoadRef = useRef(true);
  const clientNameRef = useRef('');

  const toggleSound = () => {
    const next = !soundOn;
    setSoundOn(next);
    setSoundEnabled(next);
    if (next) { unlockAudio(); playMessageDing(); }
  };

  const enablePush = async () => {
    try {
      const result = await ensurePushSubscription();
      if (result === 'ok') {
        setPushEnabled(true);
        toast.success(t('notifications.enabled'));
      } else if (result === 'denied') {
        toast.error(t('notifications.blocked'));
      } else {
        toast.error(t('notifications.unsupported'));
      }
    } catch {
      toast.error(t('notifications.blocked'));
    }
  };

  // Auto-attempt push subscription on first user gesture if not yet granted.
  useEffect(() => {
    if (!isPushSupported()) return;
    if (typeof Notification === 'undefined') return;
    if (Notification.permission !== 'default') return;
    const handler = () => {
      window.removeEventListener('click', handler);
      window.removeEventListener('keydown', handler);
      ensurePushSubscription().then(r => { if (r === 'ok') setPushEnabled(true); });
    };
    window.addEventListener('click', handler, { once: true });
    window.addEventListener('keydown', handler, { once: true });
    return () => {
      window.removeEventListener('click', handler);
      window.removeEventListener('keydown', handler);
    };
  }, []);

  const fetchChannels = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/lab/channels`);
      const list = r.data || [];
      setChannels(list);
      const total = list.reduce((s, c) => s + (c.unread_count || 0), 0);
      setAppBadge(total);
    } catch (e) {
      if (process.env.NODE_ENV !== 'production') console.debug('[LabChat]', e?.message);
    }
  }, []);

  const fetchMessages = useCallback(async () => {
    if (!channelId) return;
    try {
      const r = await axios.get(`${API}/messages/${channelId}`);
      const msgs = r.data;
      const lastInbound = [...msgs].reverse().find(m => m.direction === 'client_to_lab');
      if (lastInbound) {
        if (!initialLoadRef.current && lastInbound.id !== lastIncomingIdRef.current) {
          playMessageDing();
          notifyIncomingMessage({
            title: lastInbound.sender_name || clientNameRef.current || 'Client',
            body: (lastInbound.text || '').slice(0, 140),
            tag: `channel-${channelId}`,
          });
        }
        lastIncomingIdRef.current = lastInbound.id;
      }
      initialLoadRef.current = false;
      setMessages(msgs);
    } catch (e) {
      if (process.env.NODE_ENV !== 'production') console.debug('[LabChat]', e?.message);
    }
  }, [channelId]);

  const fetchPresence = useCallback(async () => {
    if (!channelId) return;
    try { const r = await axios.get(`${API}/presence/channel/${channelId}`); setClientOnline(r.data.client?.online || false); } catch (e) { if (process.env.NODE_ENV !== 'production') console.debug('[LabChat] presence:', e?.message); }
  }, [channelId]);

  const fetchTyping = useCallback(async () => {
    if (!channelId) return;
    try { const r = await axios.get(`${API}/typing/${channelId}`); setTypingUsers(r.data || []); } catch (e) { if (process.env.NODE_ENV !== 'production') console.debug('[LabChat] typing:', e?.message); }
  }, [channelId]);

  const markRead = useCallback(async () => {
    if (!channelId) return;
    try { await axios.post(`${API}/channels/${channelId}/mark-read`); } catch (e) { if (process.env.NODE_ENV !== 'production') console.debug('[LabChat] markRead:', e?.message); }
  }, [channelId]);

  const fetchChannelInfo = useCallback(() => {
    const ch = channels.find(c => c.channel_id === channelId);
    if (ch) {
      setClientName(ch.client_name);
      clientNameRef.current = ch.client_name;
      setClientInfo({
        first_name: ch.client_first_name || '',
        last_name: ch.client_last_name || '',
        structure: ch.client_structure || '',
        city: ch.client_city || '',
      });
    }
  }, [channels, channelId]);

  useEffect(() => { fetchChannels(); heartbeat(); }, [fetchChannels, heartbeat]);
  useEffect(() => { fetchChannelInfo(); }, [fetchChannelInfo]);

  useEffect(() => {
    if (!channelId) return;
    fetchMessages(); fetchPresence(); fetchTyping(); markRead();
    pollRef.current = setInterval(() => {
      fetchMessages(); fetchPresence(); fetchTyping(); markRead(); fetchChannels();
    }, 3000);
    heartbeatRef.current = setInterval(heartbeat, 60000);
    return () => { clearInterval(pollRef.current); clearInterval(heartbeatRef.current); clearAppBadge(); };
  }, [channelId, fetchMessages, fetchPresence, fetchTyping, markRead, fetchChannels, heartbeat]);

  useEffect(() => { messagesEndRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' }); }, [messages]);

  const sendTypingPing = () => {
    if (!channelId) return;
    const now = Date.now();
    if (now - typingThrottleRef.current < 2000) return;
    typingThrottleRef.current = now;
    axios.post(`${API}/typing`, { channel_id: channelId }).catch(() => {});
  };

  const handleSend = async (e) => {
    e.preventDefault();
    if (!newMessage.trim()) return;
    setSending(true);
    try { await axios.post(`${API}/messages`, { channel_id: channelId, text: newMessage.trim() }); setNewMessage(''); fetchMessages(); }
    catch { toast.error(t('lab.sendError')); }
    finally { setSending(false); }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 50 * 1024 * 1024) { toast.error(t('lab.fileTooLarge')); return; }
    setUploading(true);
    try {
      const fd = new FormData(); fd.append('file', file);
      const up = await axios.post(`${API}/upload`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      await axios.post(`${API}/messages`, { channel_id: channelId, text: `[Fichier] ${file.name}`, media_url: up.data.file_id, media_type: file.type });
      fetchMessages(); toast.success(t('lab.fileSent'));
    } catch { toast.error(t('lab.sendError')); }
    finally { setUploading(false); if (fileInputRef.current) fileInputRef.current.value = ''; }
  };

  const handleSearch = async () => {
    if (!searchQuery.trim()) { setSearchResults(null); return; }
    setSearching(true);
    try { const r = await axios.get(`${API}/messages/${channelId}/search?q=${encodeURIComponent(searchQuery)}`); setSearchResults(r.data); }
    catch { toast.error('Erreur'); }
    finally { setSearching(false); }
  };

  const clearSearch = () => { setSearchQuery(''); setSearchResults(null); };
  const localeKey = (i18n.language === 'zh') ? 'zh-CN' : 'en-GB';
  const formatTime = (ts) => new Date(ts).toLocaleTimeString(localeKey, { hour: '2-digit', minute: '2-digit' });
  const formatDate = (ts) => {
    const d = new Date(ts), n = new Date(), y = new Date(n); y.setDate(n.getDate() - 1);
    if (d.toDateString() === n.toDateString()) return i18n.language === 'zh' ? '今天' : 'Today';
    if (d.toDateString() === y.toDateString()) return i18n.language === 'zh' ? '昨天' : 'Yesterday';
    return d.toLocaleDateString(localeKey, { weekday: 'long', day: 'numeric', month: 'long' });
  };

  const renderMedia = (msg) => {
    if (!msg.media_url) return null;
    // Browser auto-sends the httpOnly session cookie on same-origin requests.
    const fileUrl = `${API}/files/${msg.media_url}`;
    if (msg.media_type?.startsWith('image/')) return <img src={fileUrl} alt="" className="max-w-[200px] sm:max-w-[280px] rounded-lg mt-2 cursor-pointer" onClick={() => window.open(fileUrl, '_blank')} />;
    if (msg.media_type?.startsWith('video/')) return <video src={fileUrl} controls className="max-w-[200px] sm:max-w-[300px] rounded-lg mt-2" />;
    if (msg.media_type === 'application/pdf') return <a href={fileUrl} target="_blank" rel="noreferrer" className="flex items-center gap-2 mt-2 p-2 bg-white/10 rounded-lg hover:bg-white/20"><FileText className="h-5 w-5" /><span className="text-sm underline">Open PDF</span></a>;
    return <a href={fileUrl} target="_blank" rel="noreferrer" className="flex items-center gap-2 mt-2 p-2 bg-white/10 rounded-lg"><Download className="h-4 w-4" /><span className="text-sm underline">Download</span></a>;
  };

  const getSenderLabel = (msg) => {
    if (msg.direction !== 'lab_to_client' || !msg.sender_name) return null;
    if (msg.sender_name.includes(' — ')) {
      const parts = msg.sender_name.split(' — ');
      return parts[1] || parts[0];
    }
    return msg.sender_name;
  };

  const displayMessages = searchResults !== null ? searchResults : messages;
  const grouped = displayMessages.reduce((a, m) => { const d = formatDate(m.timestamp); if (!a[d]) a[d] = []; a[d].push(m); return a; }, {});

  return (
    <div className="chat-viewport flex bg-background" data-testid="lab-chat" onClickCapture={unlockAudio} onKeyDownCapture={unlockAudio}>
      {/* ========= SIDEBAR (WhatsApp Web style) ========= */}
      <aside className="hidden lg:flex w-[320px] flex-col border-r border-border bg-muted/20 shrink-0">
        <div className="px-4 py-3 border-b border-border flex items-center justify-between">
          <div className="min-w-0">
            <p className="text-sm font-semibold truncate">{user?.name || 'Lab'}</p>
            <p className="text-xs text-muted-foreground">{t('lab.messages')}</p>
          </div>
          <div className="flex items-center gap-1 shrink-0">
            <Button variant="ghost" size="icon" className="h-8 w-8" onClick={toggleSound} title={soundOn ? 'Mute' : 'Unmute'} data-testid="sound-toggle">
              {soundOn ? <Volume2 className="h-4 w-4" /> : <VolumeX className="h-4 w-4" />}
            </Button>
            {isPushSupported() && (
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8"
                onClick={enablePush}
                title={pushEnabled ? t('notifications.enabled') : t('notifications.enable')}
                data-testid="push-toggle"
              >
                {pushEnabled ? <Bell className="h-4 w-4 text-green-600" /> : <BellOff className="h-4 w-4" />}
              </Button>
            )}
            <LanguageSwitcher langs={['en', 'zh']} className="h-7" />
            <Button variant="ghost" size="icon" className="h-8 w-8" onClick={logout} title={t('common.logout')} data-testid="lab-sidebar-logout">
              <LogOut className="h-4 w-4" />
            </Button>
          </div>
        </div>
        <ScrollArea className="flex-1">
          <div className="p-2 space-y-1" data-testid="lab-sidebar-list">
            {channels.length === 0 ? (
              <p className="text-xs text-muted-foreground text-center py-6">{t('lab.noResult')}</p>
            ) : channels.map(c => {
              const active = c.channel_id === channelId;
              const display = [c.client_first_name, c.client_last_name].filter(Boolean).join(' ') || c.client_name;
              return (
                <button
                  key={c.channel_id}
                  onClick={() => navigate(`/lab/chat/${c.channel_id}`)}
                  data-testid={`lab-sidebar-channel-${c.channel_id}`}
                  className={cn(
                    "w-full text-left px-3 py-2 rounded-lg flex items-center gap-3 transition-colors",
                    active ? "bg-primary text-primary-foreground" : "hover:bg-muted"
                  )}
                >
                  <div className="w-9 h-9 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
                    <MessageSquare className={cn("h-4 w-4", active ? "text-primary-foreground" : "text-primary")} />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium truncate">{display}</p>
                    <p className={cn("text-[11px] truncate", active ? "text-primary-foreground/80" : "text-muted-foreground")}>
                      {c.client_structure || c.client_name}
                    </p>
                  </div>
                  {c.unread_count > 0 && !active && (
                    <span className="text-[10px] font-bold bg-emerald-500 text-white rounded-full px-1.5 py-0.5">{c.unread_count}</span>
                  )}
                </button>
              );
            })}
          </div>
        </ScrollArea>
      </aside>

      {/* ========= MAIN CHAT ========= */}
      <div className="flex-1 flex flex-col min-w-0">
        <header className="border-b border-border bg-background/95 backdrop-blur flex items-center gap-2 px-3 sm:px-4 py-2 shrink-0">
          <Button variant="ghost" size="icon" className="shrink-0 lg:hidden" onClick={() => navigate('/lab')} data-testid="back-to-dashboard"><ArrowLeft className="h-5 w-5" /></Button>
          <div className="relative shrink-0">
            <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center"><MessageSquare className="h-4 w-4 text-primary" /></div>
            {clientOnline && <div className="absolute -bottom-0.5 -right-0.5 w-3 h-3 bg-green-500 rounded-full border-2 border-background" data-testid="client-online-dot" />}
          </div>
          <div className="min-w-0 flex-1">
            <p className="text-[10px] uppercase tracking-wide text-muted-foreground">{t('lab.discussingWith')}</p>
            <h2 className="font-semibold text-sm truncate" data-testid="lab-chat-client-header">
              {[clientInfo.first_name, clientInfo.last_name].filter(Boolean).join(' ') || clientName}
            </h2>
            <p className="text-[11px] text-muted-foreground truncate" data-testid="lab-chat-client-meta">
              {clientName}
              {clientInfo.structure ? ` • ${clientInfo.structure}` : ''}
              {clientInfo.city ? ` • ${clientInfo.city}` : ''}
              {' • '}
              {clientOnline ? <span className="text-green-600">{t('common.online')}</span> : t('common.offline')}
            </p>
          </div>
          <LanguageSwitcher langs={['en', 'zh']} className="ml-2 lg:hidden" />
          {isPushSupported() && (
            <Button
              variant="ghost"
              size="icon"
              className="lg:hidden"
              onClick={enablePush}
              title={pushEnabled ? t('notifications.enabled') : t('notifications.enable')}
              data-testid="push-toggle-mobile"
            >
              {pushEnabled ? <Bell className="h-4 w-4 text-green-600" /> : <BellOff className="h-4 w-4" />}
            </Button>
          )}
        </header>

        <div className="border-b border-border px-3 py-2 flex items-center gap-2">
          <Search className="h-4 w-4 text-muted-foreground shrink-0" />
          <Input value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && handleSearch()} placeholder={t('lab.search')} className="h-8 text-sm" data-testid="search-messages-input" />
          {searchResults !== null && <Button variant="ghost" size="sm" onClick={clearSearch} className="shrink-0"><X className="h-4 w-4" /></Button>}
          {searchQuery && <Button variant="outline" size="sm" onClick={handleSearch} disabled={searching} className="shrink-0" data-testid="search-btn">{searching ? <Loader2 className="h-3 w-3 animate-spin" /> : 'OK'}</Button>}
        </div>
        {searchResults !== null && <div className="px-3 py-1 bg-amber-50 border-b text-xs text-amber-800"><Badge variant="outline" className="text-xs">{searchResults.length}</Badge> {t('lab.noResult')}</div>}

        <ScrollArea className="flex-1 p-3 sm:p-4">
          <div className="max-w-3xl mx-auto space-y-4">
            {Object.entries(grouped).map(([date, msgs]) => (
              <div key={date}>
                <div className="flex items-center justify-center mb-3"><span className="text-xs text-muted-foreground bg-muted px-3 py-1 rounded-full">{date}</span></div>
                <div className="space-y-2">
                  {msgs.map((msg) => {
                    const senderLabel = getSenderLabel(msg);
                    return (
                      <div key={msg.id} className={cn("flex", msg.direction === 'lab_to_client' ? 'justify-end' : 'justify-start')}>
                        <div className={cn("max-w-[85%] sm:max-w-[75%] px-3 py-2 sm:px-4 sm:py-2.5 animate-slide-up", msg.direction === 'lab_to_client' ? 'chat-bubble-client' : 'chat-bubble-lab')}>
                          {senderLabel && <p className="text-xs font-semibold mb-1 opacity-80" data-testid="sender-label">{senderLabel}</p>}
                          {(!msg.media_url || !msg.text.startsWith('[Fichier]')) && <p className="text-base whitespace-pre-wrap break-words leading-relaxed">{msg.text}</p>}
                          {msg.media_url && msg.text.startsWith('[Fichier]') && <p className="text-base font-medium">{msg.text.replace('[Fichier] ', '')}</p>}
                          {renderMedia(msg)}
                          <p className={cn("text-xs mt-1 flex items-center gap-1", msg.direction === 'lab_to_client' ? 'text-primary-foreground/70 justify-end' : 'text-muted-foreground')}>
                            <span>{formatTime(msg.timestamp)}</span>
                            {msg.direction === 'lab_to_client' && msg.read_at && (
                              <span className="lowercase" data-testid="read-indicator">· {t('common.read')}</span>
                            )}
                          </p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
            {displayMessages.length === 0 && <div className="text-center py-12 text-muted-foreground"><MessageSquare className="h-12 w-12 mx-auto mb-3 opacity-50" /><p>{searchResults !== null ? t('lab.noResult') : t('lab.noMessage')}</p></div>}
            {typingUsers.length > 0 && (
              <div className="flex justify-start" data-testid="typing-indicator">
                <div className="px-3 py-2 rounded-2xl bg-muted text-xs text-muted-foreground italic">
                  {typingUsers[0].name || clientName} {t('common.isTyping')}
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>
        </ScrollArea>

        <div className="border-t border-border bg-background p-3 sm:p-4 pb-[env(safe-area-inset-bottom,16px)]">
          <form onSubmit={handleSend} className="max-w-3xl mx-auto flex items-center gap-2">
            <input type="file" ref={fileInputRef} onChange={handleFileUpload} className="hidden" accept="image/*,video/*,.pdf,.doc,.docx" />
            <Button type="button" variant="ghost" size="icon" className="shrink-0" onClick={() => fileInputRef.current?.click()} disabled={uploading} title="Attach file (max 50 MB)" data-testid="attach-file-btn">
              {uploading ? <Loader2 className="h-5 w-5 animate-spin" /> : <Paperclip className="h-5 w-5" />}
            </Button>
            <Input value={newMessage} onChange={(e) => { setNewMessage(e.target.value); sendTypingPing(); }} onFocus={(e) => { setTimeout(() => e.target.scrollIntoView({ block: 'center', behavior: 'smooth' }), 300); }} placeholder={t('lab.writePlaceholder')} className="flex-1 text-base h-12 sm:h-11 no-zoom-input" disabled={sending} data-testid="message-input" />
            <Button type="submit" size="icon" disabled={!newMessage.trim() || sending} className="bg-accent hover:bg-accent/90 shrink-0 h-12 w-12 sm:h-11 sm:w-11" data-testid="send-message-btn">
              {sending ? <Loader2 className="h-5 w-5 animate-spin" /> : <Send className="h-5 w-5" />}
            </Button>
          </form>
          <p className="text-[10px] text-muted-foreground text-center mt-1">{t('lab.attachHint')}</p>
        </div>
      </div>
    </div>
  );
};

export default LabChat;
